import { useState, useEffect } from "react";
import Sidebar from "../../../../components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Building, Calendar, Loader2, AlertCircle, Plus, DollarSign } from "lucide-react";
import { useAuth } from "../../../../context/AuthContext";
import DiasHabilitadosModal from "./DiasHabilitadosModal";
import CreateZonaComunModal from "./CreateZonaComunModal";
import DetailZonaComunModal from "./DetailZonaComunModal";
import { obtenerZonasComunes, ZonaComun } from "../../../../api/zonaComunService";
import Swal from "sweetalert2";

export default function ZonasComunesListPage() {
  const { session } = useAuth();
  const [zonasComunes, setZonasComunes] = useState<ZonaComun[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDiasHabilitadosModal, setShowDiasHabilitadosModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedZonaComunCodigo, setSelectedZonaComunCodigo] = useState<string>("");
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

  useEffect(() => {
    const unidadId = session?.unidad?.id;
    if (unidadId) {
      loadZonasComunes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.unidad?.id]);

  // Función para cargar zonas comunes desde el API
  const loadZonasComunes = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const unidadCodigo = session?.unidad?.id || "";
      if (!unidadCodigo) {
        setZonasComunes([]);
        return;
      }

      const payload = {
        unidad_residencial_codigo: unidadCodigo,
        zona_comun_codigo: "",
      };

      const response = await obtenerZonasComunes(payload);
      
      if (response.code === "1" && response.object_) {
        setZonasComunes(response.object_);
        setImageErrors(new Set()); // Limpiar errores de imágenes al recargar
      } else {
        setZonasComunes([]);
        setError(response.message || "No se encontraron zonas comunes");
      }
    } catch (error) {
      console.error("Error al cargar zonas comunes:", error);
      setError("Error al cargar las zonas comunes");
      setZonasComunes([]);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar las zonas comunes",
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDiasHabilitados = (zonaComunCodigo: string) => {
    setSelectedZonaComunCodigo(zonaComunCodigo);
    setShowDiasHabilitadosModal(true);
  };

  const handleCloseDiasHabilitadosModal = () => {
    setShowDiasHabilitadosModal(false);
    setSelectedZonaComunCodigo("");
  };

  const handleCloseCreateModal = (created: boolean) => {
    setShowCreateModal(false);
    if (created) {
      // Recargar la lista de zonas comunes
      loadZonasComunes();
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100 text-gray-900">
      <Sidebar />
      <main className="flex-1 p-4 sm:p-6 md:ml-64 transition-all">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-military-600">
            Listado Zonas Comunes
          </h2>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-military-500 hover:bg-military-600 text-white shadow-lg font-semibold flex items-center gap-2"
          >
            <Plus size={20} />
            <span className="hidden sm:inline">Agregar Zona Común</span>
            <span className="sm:hidden">Agregar</span>
          </Button>
        </div>

        <Card className="shadow-lg border-l-4 border-military-500">
          <CardHeader>
            <CardTitle className="text-xl text-military-600 flex items-center gap-2">
              <Building size={24} />
              Zonas Comunes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-military-600" />
                <span className="ml-2 text-gray-600">Cargando zonas comunes...</span>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-8">
                <AlertCircle className="h-8 w-8 text-red-500" />
                <span className="ml-2 text-gray-600">{error}</span>
              </div>
            ) : zonasComunes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Building size={48} className="mx-auto mb-4 text-gray-400" />
                <p>No hay zonas comunes disponibles.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {zonasComunes.map((zona) => (
                  <Card
                    key={zona.zco_codigo}
                    className="bg-white rounded-xl border-2 border-gray-200 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer hover:border-military-400 transform hover:-translate-y-1"
                    onClick={() => {
                      setSelectedZonaComunCodigo(zona.zco_codigo);
                      setShowDetailModal(true);
                    }}
                  >
                    <div className="w-full h-56 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 relative group flex items-center justify-center">
                      {zona.zco_ruta_foto_principal && !imageErrors.has(zona.zco_codigo) ? (
                        <img
                          src={zona.zco_ruta_foto_principal}
                          alt={zona.zco_nombre}
                          className="max-w-full max-h-full object-contain transition-transform duration-300"
                          onError={() => {
                            setImageErrors((prev) => new Set(prev).add(zona.zco_codigo));
                          }}
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center text-gray-400 h-full p-6">
                          <div className="bg-white/50 rounded-full p-4 mb-3">
                            <Building size={40} />
                          </div>
                          <span className="text-sm font-medium">Sin imagen</span>
                        </div>
                      )}
                    </div>
                    
                    <CardContent className="p-5">
                      <div className="mb-4">
                        <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-1">
                          {zona.zco_nombre}
                        </h3>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-3 leading-relaxed">
                          {zona.zco_descripcion}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 mb-3">
                          <span className="flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-50 px-2.5 py-1 rounded-full">
                            <Calendar size={14} className="text-military-600" />
                            {zona.zco_dia_mantenimiento_nombre}
                          </span>
                          <span className="flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-50 px-2.5 py-1 rounded-full">
                            <DollarSign size={14} className="text-green-600" />
                            ${zona.zco_valor_alquiler.toLocaleString('es-CO')}
                          </span>
                        </div>
                        <div>
                          <span className={`inline-block px-3 py-1.5 rounded-full text-xs font-bold ${
                            zona.zco_estatus === "D" 
                              ? "bg-green-100 text-green-800 border border-green-300" 
                              : zona.zco_estatus === "M"
                              ? "bg-yellow-100 text-yellow-800 border border-yellow-300"
                              : "bg-red-100 text-red-800 border border-red-300"
                          }`}>
                            {zona.zco_estatus_nombre}
                          </span>
                        </div>
                      </div>
                      
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDiasHabilitados(zona.zco_codigo);
                        }}
                        className="w-full bg-gradient-to-r from-military-500 to-military-600 hover:from-military-600 hover:to-military-700 text-white shadow-lg font-bold flex items-center justify-center gap-2 py-2.5 transition-all"
                      >
                        <Calendar size={18} />
                        Días Habilitados
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <DiasHabilitadosModal
          isOpen={showDiasHabilitadosModal}
          zonaComunCodigo={selectedZonaComunCodigo}
          onClose={handleCloseDiasHabilitadosModal}
        />

        <CreateZonaComunModal
          isOpen={showCreateModal}
          onClose={handleCloseCreateModal}
        />

        <DetailZonaComunModal
          isOpen={showDetailModal}
          zonaComunCodigo={selectedZonaComunCodigo}
          onClose={(shouldReload) => {
            setShowDetailModal(false);
            setSelectedZonaComunCodigo("");
            if (shouldReload) {
              loadZonasComunes();
            }
          }}
        />
      </main>
    </div>
  );
}

